import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Clock, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { addRecentSearch, getRecentSearches } from "@/lib/watchlist";
import { shortHash } from "@/lib/format";

const NOT_FOUND_MSG = "We could not find this address, transaction, token, or block. Please check the value and try again.";

export default function SearchBar({ compact = false }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [focused, setFocused] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setFocused(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    setError("");
    if (q.trim().length >= 2 && !q.startsWith("0x") && !/^\d+$/.test(q)) {
      debounceRef.current = setTimeout(() => {
        api.get("/search", { params: { q } })
          .then((r) => setSuggestions(r.data.items || []))
          .catch(() => setSuggestions([]));
      }, 350);
    } else {
      setSuggestions([]);
    }
  }, [q]);

  const goTo = (type, value, label) => {
    addRecentSearch({ type, value, label: label || value });
    setFocused(false);
    setQ("");
    if (type === "transaction") navigate(`/tx/${value}`);
    else if (type === "block") navigate(`/block/${value}`);
    else if (type === "token") navigate(`/token/${value}`);
    else navigate(`/address/${value}`);
  };

  const submit = async (e) => {
    e?.preventDefault();
    const query = q.trim();
    if (!query) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.get("/search", { params: { q: query } });
      if (data.redirect) {
        goTo(data.redirect.type, data.redirect.value);
      } else if (data.items?.length) {
        const it = data.items[0];
        goTo(it.token_url ? "token" : it.address_hash ? "address" : "address", it.address_hash || it.address, it.name);
      } else {
        setError(NOT_FOUND_MSG);
      }
    } catch {
      setError(NOT_FOUND_MSG);
    } finally {
      setBusy(false);
    }
  };

  const recents = getRecentSearches();
  const showDropdown = focused && (suggestions.length > 0 || (q === "" && recents.length > 0));

  return (
    <div ref={boxRef} className="relative w-full">
      <form onSubmit={submit} className="relative">
        <Search size={compact ? 14 : 18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          data-testid={compact ? "header-search-input" : "hero-search-input"}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setFocused(true)}
          placeholder={compact ? "Search GMScan…" : "Search by wallet address, transaction hash, block number, or token…"}
          className={`w-full bg-card border rounded-xl outline-none transition-colors duration-200 focus:border-primary placeholder:text-muted-foreground ${
            compact ? "pl-9 pr-9 py-2 text-sm" : "pl-11 pr-12 py-4 text-sm sm:text-base shadow-lg shadow-primary/5"
          }`}
        />
        <button
          type="submit"
          data-testid={compact ? "header-search-submit" : "hero-search-submit"}
          className={`absolute right-2 top-1/2 -translate-y-1/2 rounded-lg bg-primary text-primary-foreground flex items-center justify-center transition-colors duration-200 hover:opacity-90 ${compact ? "h-6 w-6" : "h-9 w-9"}`}
        >
          {busy ? <Loader2 size={compact ? 12 : 16} className="animate-spin" /> : <Search size={compact ? 12 : 16} />}
        </button>
      </form>

      {error && (
        <p data-testid="search-error" className="mt-2 text-xs text-destructive">{error}</p>
      )}

      {showDropdown && (
        <div className="absolute top-full mt-2 w-full rounded-xl border bg-popover/95 backdrop-blur-xl shadow-xl z-50 overflow-hidden">
          {q === "" && recents.length > 0 && (
            <div>
              <p className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Recent searches</p>
              {recents.map((r, i) => (
                <button key={i} data-testid={`recent-search-${i}`} onClick={() => goTo(r.type, r.value, r.label)}
                  className="w-full text-left px-4 py-2 hover:bg-muted flex items-center gap-2 text-sm transition-colors duration-150">
                  <Clock size={12} className="text-muted-foreground shrink-0" />
                  <span className="truncate font-mono text-xs">{r.label !== r.value ? r.label : shortHash(r.value, 8)}</span>
                  <span className="ml-auto text-[10px] text-muted-foreground uppercase">{r.type}</span>
                </button>
              ))}
            </div>
          )}
          {suggestions.map((s, i) => (
            <button key={i} data-testid={`search-suggestion-${i}`}
              onClick={() => goTo(s.token_url ? "token" : "address", s.address_hash || s.address, s.name)}
              className="w-full text-left px-4 py-2.5 hover:bg-muted flex items-center gap-3 transition-colors duration-150">
              {s.icon_url ? <img src={s.icon_url} alt="" className="h-5 w-5 rounded-full" /> : <span className="h-5 w-5 rounded-full bg-secondary" />}
              <div className="min-w-0">
                <p className="text-sm truncate">{s.name} {s.symbol ? <span className="text-muted-foreground">({s.symbol})</span> : null}</p>
                <p className="text-[10px] font-mono text-muted-foreground truncate">{s.address_hash || s.address}</p>
              </div>
              <span className="ml-auto text-[10px] text-muted-foreground uppercase shrink-0">{s.type}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
